import React, { useState, useEffect } from 'react';
import {
  Stack,
  Text,
  ProgressIndicator,
  Icon,
  Spinner,
  SpinnerSize,
  MessageBar,
  MessageBarType,
} from '@fluentui/react';
import { tokenLimitsService, OrganizationUsage } from '../../services/tokenLimitsService';

interface OrganizationUsageProgressProps {
  className?: string;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const getProgressColor = (percent: number, blockAt?: number): string => {
  const threshold = blockAt || 100;
  if (percent >= threshold) return '#e74c3c'; // Red - blocked
  if (percent >= threshold * 0.9) return '#e67e22'; // Orange - warning
  if (percent >= threshold * 0.75) return '#f39c12'; // Yellow - caution
  return '#27ae60'; // Green - safe
};

const getStatusText = (percent: number, blockAt?: number): string => {
  const threshold = blockAt || 100;
  if (percent >= threshold) return 'Limit Reached';
  if (percent >= threshold * 0.9) return 'Almost at Limit';
  if (percent >= threshold * 0.75) return 'Approaching Limit';
  return 'Within Limit';
};

export const OrganizationUsageProgress: React.FC<OrganizationUsageProgressProps> = ({ 
  className
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgUsage, setOrgUsage] = useState<OrganizationUsage | null>(null);

  useEffect(() => {
    loadOrganizationUsage();
    // Refresh every 5 minutes
    const interval = setInterval(loadOrganizationUsage, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadOrganizationUsage = async () => {
    try {
      setError(null);
      const data = await tokenLimitsService.getOrganizationUsage();
      setOrgUsage(data);
    } catch (err) {
      console.error('Failed to load organization usage:', err);
      setError('Failed to load usage data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.4)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
        textAlign: 'center'
      }} className={className}>
        <Spinner size={SpinnerSize.medium} label="Loading usage data..." />
      </div>
    );
  }

  if (error) {
    return (
      <MessageBar messageBarType={MessageBarType.error} className={className}>
        {error}
      </MessageBar>
    );
  }

  if (!orgUsage || !orgUsage.organization) {
    // No organization limit configured - don't show anything
    return null;
  }

  const organization_limit = orgUsage.organization;
  const orgPercent = (organization_limit.used_tokens / organization_limit.max_tokens) * 100;
  const orgColor = getProgressColor(orgPercent, 100); // Default block at 100%
  const orgStatus = getStatusText(orgPercent, 100);

  return (
    <div style={{
      background: 'rgba(255, 255, 255, 0.4)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      borderRadius: '24px',
      padding: '32px',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    }} className={className}>
      <Stack horizontal verticalAlign="center" style={{ marginBottom: '24px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea, #764ba2)',
          borderRadius: '16px',
          padding: '12px',
          marginRight: '16px'
        }}>
          <Icon iconName="Gauge" style={{ fontSize: '24px', color: '#ffffff' }} />
        </div>
        <Stack>
          <Text variant="xLarge" style={{ fontWeight: '700', color: '#2d3748' }}>
            Token Usage Limits
          </Text>
          <Text variant="small" style={{ color: '#718096' }}>
            Track your organization's AI token consumption
          </Text>
        </Stack>
      </Stack>

      {/* Organization Usage - Always show prominently */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.6)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '20px',
        border: `2px solid ${orgColor}20`
      }}>
        <Stack horizontal horizontalAlign="space-between" verticalAlign="center" style={{ marginBottom: '16px' }}>
          <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
            <Icon iconName="Org" style={{ fontSize: '20px', color: orgColor }} />
            <Text variant="large" style={{ fontWeight: '600', color: '#2d3748' }}>
              Organization
            </Text>
          </Stack>
          <div style={{
            background: `${orgColor}20`,
            padding: '4px 12px',
            borderRadius: '12px',
          }}>
            <Text variant="small" style={{ color: orgColor, fontWeight: '600' }}>
              {orgStatus}
            </Text>
          </div>
        </Stack>
        
        <ProgressIndicator 
          percentComplete={Math.min(orgPercent, 100) / 100}
          barHeight={12}
          styles={{
            progressBar: {
              background: orgPercent >= 100 
                ? 'linear-gradient(90deg, #e74c3c, #c0392b)' 
                : `linear-gradient(90deg, ${orgColor}, ${orgColor}dd)`
            },
            progressTrack: {
              background: 'rgba(0,0,0,0.1)'
            }
          }}
        />
        
        <Stack horizontal horizontalAlign="space-between" style={{ marginTop: '12px' }}>
          <Text variant="medium" style={{ color: '#4a5568' }}>
            <strong>{formatNumber(organization_limit.used_tokens)}</strong> tokens used
          </Text>
          <Text variant="medium" style={{ color: '#4a5568' }}>
            <strong>{formatNumber(organization_limit.max_tokens)}</strong> max ({orgPercent.toFixed(1)}%)
          </Text>
        </Stack>
        
        {organization_limit.reset_period !== 'none' && (
          <Text variant="small" style={{ color: '#718096', marginTop: '8px', display: 'block' }}>
            Resets {organization_limit.reset_period}
          </Text>
        )}
      </div>

      {/* User and Group Limits - Removed: OrganizationUsage only shows org-level data */}

      {/* Blocked Warning */}
      {organization_limit.is_blocked && (
        <MessageBar 
          messageBarType={MessageBarType.blocked}
          style={{ marginTop: '16px', borderRadius: '12px' }}
        >
          <strong>Token Limit Reached:</strong> Your organization has reached its token limit. 
          Please contact your administrator for assistance.
        </MessageBar>
      )}
    </div>
  );
};

export default OrganizationUsageProgress;
